import { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { Plus } from "lucide-react";

import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import type { ProjectTag } from "@/types/api";
import { CreateTagSurface } from "./create-tag-surface";

interface TagSelectorProps {
  availableTags: ProjectTag[];
  selectedTags: string[];
  /** IDs that were just selected by voice fill and haven't been
   * acknowledged by the user yet. Render with tertiary tint + leading dot. */
  aiSelectedTagIds?: string[];
  /** Ordered list of tag ids that voice fill has promoted to the front
   * of the picker. The parent maintains this list across the modal's
   * lifetime — once a pill is promoted, it stays at the front even
   * after the user "accepts" (clearing the tint) or later toggles it
   * off. User-toggled (non-AI) selections are NOT in this list and
   * stay in their original `availableTags` position. */
  aiPromotedOrder?: string[];
  /** Called when the user taps an AI-selected pill for the first time.
   * Implementations should clear that id from `aiSelectedTagIds`. The tap
   * does NOT toggle selection in this case — it acknowledges the AI choice.
   * Subsequent taps fall through to the normal `onChange` toggle. */
  onAcceptAiTag?: (id: string) => void;
  /** When true, show a small inline "AI is choosing tags…" indicator
   * next to the "Tags" label — covers the silent window while voice
   * transcription is in flight. */
  showAiChoosingHint?: boolean;
  onChange: (next: string[]) => void;
  disabled?: boolean;
  projectId: string;
  onTagCreated?: (tag: ProjectTag) => void;
  /** When true and the selector is in edit mode, render a `+ Tag` pill that
   * opens a create-tag surface. Gated upstream by the `tag:Create` project
   * permission and online status. */
  canCreateTag?: boolean;
  /** Renders the "Use previous tags" switch beside the Tags label. Supplied
   * on every new record. Presentational — the parent owns the remembered set
   * and applies/removes the ids, because the selection lives in its form
   * state. `count` is how many remembered ids still exist in `availableTags`;
   * at 0 the switch still renders, inert, and explains itself when tapped
   * rather than vanishing. See US-005. */
  previousTags?: {
    count: number;
    enabled: boolean;
    onChange: (next: boolean) => void;
  };
}

export function TagSelector({
  availableTags,
  selectedTags,
  aiSelectedTagIds,
  aiPromotedOrder,
  onAcceptAiTag,
  showAiChoosingHint,
  onChange,
  disabled,
  projectId,
  onTagCreated,
  canCreateTag = false,
  previousTags,
}: TagSelectorProps) {
  const [createOpen, setCreateOpen] = useState(false);

  // Tapping the switch while nothing is remembered surfaces a transient hint
  // instead of doing nothing. Held here rather than in the parent: it is
  // presentation, and it must clear itself on unmount.
  const [showNoPreviousHint, setShowNoPreviousHint] = useState(false);
  const noPreviousHintTimer = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  useEffect(
    () => () => {
      if (noPreviousHintTimer.current) {
        clearTimeout(noPreviousHintTimer.current);
      }
    },
    [],
  );
  const flashNoPreviousHint = () => {
    setShowNoPreviousHint(true);
    if (noPreviousHintTimer.current) clearTimeout(noPreviousHintTimer.current);
    noPreviousHintTimer.current = setTimeout(
      () => setShowNoPreviousHint(false),
      3000,
    );
  };

  const aiSet = useMemo(
    () => new Set(aiSelectedTagIds ?? []),
    [aiSelectedTagIds],
  );

  // Render order: pills whose ids appear in aiPromotedOrder come first
  // (in that exact order); everything else follows in availableTags
  // order. User toggles do NOT affect ordering — only voice fill does,
  // and only via the parent updating aiPromotedOrder.
  const orderedTags = useMemo(() => {
    const promotedIds = aiPromotedOrder ?? [];
    if (promotedIds.length === 0) return availableTags;

    const byId = new Map(availableTags.map((t) => [t.id, t]));
    const promoted: ProjectTag[] = [];
    const promotedSet = new Set<string>();
    for (const id of promotedIds) {
      const tag = byId.get(id);
      if (tag) {
        promoted.push(tag);
        promotedSet.add(id);
      }
    }
    const rest = availableTags.filter((t) => !promotedSet.has(t.id));
    return [...promoted, ...rest];
  }, [availableTags, aiPromotedOrder]);

  // FLIP-style smooth reorder. Captures pill positions across renders and
  // applies an inverse transform so each pill appears to travel to its new
  // spot rather than jump.
  const containerRef = useRef<HTMLDivElement>(null);
  const prevRectsRef = useRef<Map<string, DOMRect>>(new Map());

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    ) {
      prevRectsRef.current = new Map();
      return;
    }

    const items =
      container.querySelectorAll<HTMLElement>("[data-tag-id]");
    const newRects = new Map<string, DOMRect>();
    items.forEach((el) => {
      const id = el.dataset.tagId;
      if (id) newRects.set(id, el.getBoundingClientRect());
    });

    const oldRects = prevRectsRef.current;
    items.forEach((el) => {
      const id = el.dataset.tagId;
      if (!id) return;
      const oldRect = oldRects.get(id);
      const newRect = newRects.get(id);
      if (!oldRect || !newRect) return;
      const dx = oldRect.left - newRect.left;
      const dy = oldRect.top - newRect.top;
      if (dx === 0 && dy === 0) return;
      el.style.transition = "none";
      el.style.transform = `translate(${dx}px, ${dy}px)`;
    });

    requestAnimationFrame(() => {
      items.forEach((el) => {
        if (el.style.transform) {
          el.style.transition =
            "transform 500ms cubic-bezier(.2, .7, .3, 1)";
          el.style.transform = "";
        }
      });
    });

    prevRectsRef.current = newRects;
  }, [orderedTags]);

  // Pop animation on freshly-AI pills. Triggered AFTER the FLIP transform
  // settles (~500ms) so the two animations don't clobber each other on
  // the same `transform` property. No-ops under reduced-motion (the
  // .voice-pill-pop CSS rule itself disables the keyframe).
  const prevAiSetRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const currentAi = new Set(aiSelectedTagIds ?? []);
    const newlyAi: string[] = [];
    currentAi.forEach((id) => {
      if (!prevAiSetRef.current.has(id)) newlyAi.push(id);
    });
    prevAiSetRef.current = currentAi;

    if (newlyAi.length === 0) return;
    const fireDelay = 520; // run after the 500ms FLIP transition settles
    const fireTimer = setTimeout(() => {
      const container = containerRef.current;
      if (!container) return;
      newlyAi.forEach((id) => {
        const pill = container.querySelector<HTMLElement>(
          `[data-tag-id="${CSS.escape(id)}"]`,
        );
        if (!pill) return;
        pill.classList.add("voice-pill-pop");
        // Remove the class once the animation finishes so a later re-add
        // can replay it. animationend would work too but a fixed timeout
        // is robust to the prefers-reduced-motion case (where the class
        // is present but the keyframe is disabled).
        setTimeout(() => pill.classList.remove("voice-pill-pop"), 400);
      });
    }, fireDelay);
    return () => clearTimeout(fireTimer);
  }, [aiSelectedTagIds]);

  if (disabled) {
    if (availableTags.length === 0) {
      return (
        <div className="space-y-2">
          <Label>Tags</Label>
          <p className="text-sm text-muted-foreground">—</p>
        </div>
      );
    }
    return (
      <div className="space-y-2">
        <Label>Tags</Label>
        <div className="flex flex-wrap gap-1.5">
          {availableTags.map((t) => (
            <Badge key={t.id} variant="secondary">
              {t.name}
            </Badge>
          ))}
        </div>
      </div>
    );
  }

  const handlePillClick = (id: string) => {
    if (aiSet.has(id) && onAcceptAiTag) {
      // First tap on an AI-selected pill: accept the choice. Pill stays
      // selected; the tint clears via the parent's aiSelectedTagIds update.
      onAcceptAiTag(id);
      return;
    }
    const next = selectedTags.includes(id)
      ? selectedTags.filter((v) => v !== id)
      : [...selectedTags, id];
    onChange(next);
  };

  const handleCreated = (tag: ProjectTag) => {
    onTagCreated?.(tag);
    if (!selectedTags.includes(tag.id)) {
      onChange([...selectedTags, tag.id]);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label>Tags</Label>
        {showAiChoosingHint && (
          <span
            data-ai-choosing-hint="true"
            className="inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wide text-tertiary-ink"
          >
            <span
              aria-hidden="true"
              className="h-1.5 w-1.5 animate-pulse rounded-full bg-tertiary"
            />
            AI is choosing tags…
          </span>
        )}
        {previousTags && availableTags.length > 0 && (
          // Not a <label>: a Radix Switch renders a <button>, which is not a
          // labelable element, so wrapping it would give text that looks
          // tappable but isn't. The switch carries its own accessible name.
          <span
            data-testid="previous-tags-toggle"
            className="relative ml-auto flex items-center gap-2 text-xs text-muted-foreground"
          >
            {showNoPreviousHint && (
              <span
                role="status"
                data-testid="previous-tags-empty-hint"
                className="absolute -top-6 right-0 whitespace-nowrap rounded-md bg-muted px-2 py-1 text-[10px] text-muted-foreground shadow-sm"
              >
                Previous tags not found
              </span>
            )}
            <span aria-hidden="true">Use previous tags</span>
            <Switch
              checked={previousTags.enabled}
              // `aria-disabled`, never `disabled`: a disabled button swallows
              // the click, and the whole point of the inert state is that
              // tapping it explains why it is inert.
              aria-disabled={previousTags.count === 0 || undefined}
              onCheckedChange={(next) => {
                if (previousTags.count === 0) {
                  flashNoPreviousHint();
                  return;
                }
                previousTags.onChange(next);
              }}
              className={cn(previousTags.count === 0 && "opacity-50")}
              aria-label="Use previous tags"
            />
          </span>
        )}
      </div>
      {availableTags.length === 0 && !canCreateTag ? (
        <p className="text-xs text-muted-foreground">
          No tags available for this project.
        </p>
      ) : (
        <div ref={containerRef} className="flex flex-wrap gap-1.5">
          {orderedTags.map((t) => {
            const isSelected = selectedTags.includes(t.id);
            const isAi = aiSet.has(t.id);
            return (
              <button
                key={t.id}
                type="button"
                data-tag-id={t.id}
                data-ai-source={isAi ? "true" : undefined}
                aria-pressed={isSelected}
                aria-label={
                  isAi ? `${t.name}, selected by voice` : undefined
                }
                onClick={() => handlePillClick(t.id)}
                className={cn(
                  "rounded-full border px-2.5 py-0.5 text-xs transition-colors",
                  isAi
                    ? "inline-flex items-center gap-1.5 border-tertiary/40 bg-tertiary/10 text-tertiary-ink hover:bg-tertiary/15"
                    : isSelected
                      ? "border-tertiary bg-tertiary text-tertiary-foreground"
                      : "border-border bg-transparent hover:bg-accent",
                )}
              >
                {isAi && (
                  <span
                    aria-hidden="true"
                    className="h-1.5 w-1.5 rounded-full bg-tertiary"
                  />
                )}
                {t.name}
              </button>
            );
          })}
          {canCreateTag && (
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              aria-label="Create new tag"
              data-testid="tag-selector-create"
              className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2.5 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              <Plus className="h-3 w-3" aria-hidden="true" />
              Tag
            </button>
          )}
        </div>
      )}
      {canCreateTag && (
        <CreateTagSurface
          open={createOpen}
          onOpenChange={setCreateOpen}
          projectId={projectId}
          availableTags={availableTags}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}
